import { addLog } from 'assets/components/feedback/Feedback';
import useApi from 'assets/hooks/api/useApi';
import usePageRouter from 'assets/hooks/pageRouter/usePageRouter';
import { getLocales, Language } from 'assets/locales/Locale';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import { useDataProviderPartition } from 'assets/providers/data/DataProvider';
import { combineStrings, replaceStringTemplateWithValues } from 'assets/utils/data/String';
import { defaultLimit, defaultOffset, HttpStatus } from 'config/Api.Config';
import { cloneDeep, differenceBy, xorBy } from 'lodash';
import dealerDeleteProductEnhancementDocumentApi from 'models/dealerManagement/dealer/deleteProductEnhancementDocument/Api.Dealer.DeleteProductEnhancementDocument';
import dealerUploadProductEnhancementDocumentApi from 'models/dealerManagement/dealer/uploadProductEnhancementDocument/Api.Dealer.UploadProductEnhancementDocument';
import ProductEnhancementDealerDocument from 'models/dealerManagement/productEnhancementDealerDocument/Model.ProductEnhancementDealerDocument';
import ProductEnhancementDocument from 'models/productManagement/productEnhancementDocument/ProductEnhancementDocument';
import productEnhancementDocumentListDealerDocumentsApi from 'models/productManagement/productEnhancementDocument/listDealers/Api.ProductEnhancementDocument.ListDealers';
import { useEffect, useMemo, useState } from 'react';
import { ProductEnhancementDealerDocumentAddForm } from './sections/ProductEnhancementDocument.DealerDocuments.Add';
import { productEnhancementDocumentIndexRoute } from '../ProductEnhancementDocument.Index';
import {
  documentTranslationDeleteApi,
  documentTranslationListApi,
  documentTranslationSaveApi,
} from 'models/localization/documentTranslation/Model.DocumentTranslation';
import { convertToArray } from 'assets/utils/data/Data';

export enum ProductEnhancementDealerDocumentsAction {
  ADD = 'add',
  DELETE = 'delete',
}
export type VerifyProductEnhancementDocumentsAction = Utils.VerifyExtends<
  Module.ProductManagement.ProductEnhancementDocument.Actions,
  Utils.ValueOf<typeof ProductEnhancementDealerDocumentsAction>
>;
export default function useProductEnhancementDealerDocument() {
  const { permissions } = useAuthStore();
  const pageRouter = usePageRouter<
    Module.ProductManagement.ProductEnhancementDocument.Params,
    Module.ProductManagement.ProductEnhancementDocument.Query
  >({
    route: productEnhancementDocumentIndexRoute,
  });
  const documentId = pageRouter.params.id ? parseInt(pageRouter.params.id) : null;

  const { lang } = getLocales();
  const [currentView, setView] = useState<ProductEnhancementDealerDocumentsAction | undefined>();
  const [selectedItems, setSelectedItems] = useState<ProductEnhancementDealerDocument[]>([]);
  const [selectedTranslatedItems, setSelectedTranslatedItems] = useState<Model.IDocumentTranslation[]>([]);
  const [{ limit, offset, distributorId }, setDealerDocumentFilters] = useState({
    limit: defaultLimit,
    offset: defaultOffset,
    distributorId: undefined,
  });

  const { payload: currentDocument, isExecuting: isLoadingProductDocument } = useApi(
    {
      action: ProductEnhancementDocument.find,
      body: { id: documentId },
      default: null,
      wait: !documentId,
    },
    [documentId]
  );

  const canView = permissions.EDIT_PRODUCT;
  const listDealerDocumentsApi = useApi({
    action: productEnhancementDocumentListDealerDocumentsApi,
    default: { productEnhancementDocumentId: documentId },
    body: {
      productEnhancementDocumentId: documentId,
    } as Api.Core.ProductEnhancementDocument.ListDealerDocuments.IRequest,
    wait: !canView,
  });

  const documentIds = useMemo(
    () => listDealerDocumentsApi.payload?.data.map((d) => d.id),
    [listDealerDocumentsApi.payload]
  );
  const listDocumentTranslationsApi = useApi(
    {
      action: documentTranslationListApi,
      body: { documentType: 'ProductEnhancementDealerDocument', documentId: documentIds },
      wait: !canView || !documentIds?.length,
    },
    [canView, documentIds]
  );
  function reloadTranslatedDocs() {
    listDocumentTranslationsApi.execute((b) => b);
  }

  const handleUploadDealerDocumentSubmit = async (formData: ProductEnhancementDealerDocumentAddForm) => {
    const dealers = formData.dealers || [];
    let uploadedFiles = 0;
    let totalFiles = 0;

    for (const dealer of dealers) {
      const docUploadResponse = await dealerUploadProductEnhancementDocumentApi(
        {
          id: dealer,
          productEnhancementId: formData.productEnhancementId,
          productEnhancementDocumentId: formData.productEnhancementDocumentId,
        },
        { formFile: formData.formFile, languageIsoCode: Language.english }
      );

      if (docUploadResponse.status === HttpStatus.ok) {
        uploadedFiles += 1;
        if (formData.translatedFile) {
          const translatedDocUploadResponse = await documentTranslationSaveApi({
            documentId: docUploadResponse.payload.documentId,
            documentType: 'ProductEnhancementDealerDocument',
            languageIsoCode: Language.french,
            formFile: formData.translatedFile,
          });
          if (translatedDocUploadResponse.status === HttpStatus.ok) {
            uploadedFiles += 1;
          }
          totalFiles += 1;
        }
      }
    }
    totalFiles += 1;

    listDealerDocumentsApi.execute((body) => body);

    if (totalFiles !== uploadedFiles) {
      addLog({
        warning: combineStrings(
          ' ',
          `Some files did not get uploaded!`,
          totalFiles > 0 ? `${uploadedFiles}/${totalFiles} files uploaded!` : null
        ),
      });
    } else {
      addLog({
        success: replaceStringTemplateWithValues(lang.wereSuccessfullyUploaded, {
          entity: lang.productDocuments,
        }),
      });
    }

    setView(undefined);
  };

  const handleDeleteDealerDocumentSubmit = async () => {
    const documents = selectedItems || [];
    const translatedDocs = cloneDeep(selectedTranslatedItems) || [];
    let deletedFiles = 0;
    let totalFiles = 0;

    for (const document of documents) {
      const docDeleteReponse = await dealerDeleteProductEnhancementDocumentApi({
        id: document.dealerId,
        productEnhancementDealerDocumentId: document.id,
      });
      if (docDeleteReponse.status === HttpStatus.ok) {
        deletedFiles += 1;
      }
      totalFiles += 1;

      const tempDocs = listDocumentTranslationsApi.payload?.data.filter((d) => d.documentId === document.id);
      const diffDocs = differenceBy(tempDocs, translatedDocs, (it) => it.id);
      translatedDocs.push(...diffDocs);
    }
    for (const translatedDoc of translatedDocs) {
      const translationDocDeleteReponse = await documentTranslationDeleteApi({
        id: translatedDoc.id,
      });
      if (translationDocDeleteReponse.status === HttpStatus.ok) {
        deletedFiles += 1;
      }
      totalFiles += 1;
    }

    listDealerDocumentsApi.execute((body) => body);

    if (totalFiles !== deletedFiles) {
      addLog({
        warning: combineStrings(
          ' ',
          `Some files did not get deleted!`,
          totalFiles > 0 ? `${deletedFiles}/${totalFiles} files deleted!` : null
        ),
      });
    } else {
      addLog({
        success: replaceStringTemplateWithValues(lang.wereSuccessfullyDeleted, {
          entity: lang.productDocuments,
        }),
      });
    }

    setView(undefined);
    setSelectedItems([]);
    setSelectedTranslatedItems([]);
  };

  const toggleItem = (document: ProductEnhancementDealerDocument) => {
    const newSelectedItemList = xorBy(selectedItems || [], [document], 'dealerId');
    setSelectedItems(newSelectedItemList);
  };
  const toggleTranslatedItem = (translatedDoc: Utils.SingleOrArray<Model.IDocumentTranslation>) => {
    const newSelectedItemList = xorBy(selectedTranslatedItems || [], convertToArray(translatedDoc), 'documentId');
    setSelectedTranslatedItems(newSelectedItemList);
  };

  const toggleSelectAll = (selectAll: boolean) => {
    if (selectAll) {
      setSelectedItems(listDealerDocumentsApi.payload?.data ?? []);
    } else {
      setSelectedItems([]);
    }
  };
  const toggleSelectAllTranslations = (selectAll: boolean) => {
    if (selectAll) {
      setSelectedTranslatedItems(listDocumentTranslationsApi.payload?.data ?? []);
    } else {
      setSelectedTranslatedItems([]);
    }
  };

  const [filteredDealerDocuments, filteredDealerDocumentsCount] = useMemo(() => {
    const filteredList = listDealerDocumentsApi.payload?.data?.filter(
      (dealerDocument) => !distributorId || dealerDocument.dealer.representativeId === distributorId
    );

    return [filteredList?.slice(offset * limit, offset * limit + limit), filteredList?.length];
  }, [listDealerDocumentsApi.payload, limit, offset, distributorId]);

  useEffect(() => {
    setSelectedItems([]);
  }, [listDealerDocumentsApi.payload?.data]);

  return {
    canView,
    currentView,
    pageRouter,
    productEnhancementDocument: currentDocument,
    selectedItems,
    toggleItem,
    toggleSelectAll,
    selectedTranslatedItems,
    toggleTranslatedItem,
    toggleSelectAllTranslations,
    isLoadingProductDocument,
    handleDeleteDealerDocumentSubmit,
    handleUploadDealerDocumentSubmit,
    setView,
    permissions,
    fullDealerDocumentList: listDealerDocumentsApi?.payload?.data,
    translatedDocumentList: listDocumentTranslationsApi?.payload?.data ?? [],
    reloadTranslatedDocs,
    dealerDocumentList: {
      limit,
      offset,
      distributorId,
      setDealerDocumentFilters,
      data: filteredDealerDocuments,
      count: filteredDealerDocumentsCount,
      applyFilters: ({ distributorId }: { distributorId?: number }) => {
        setDealerDocumentFilters((old) => ({ ...old, distributorId }));
      },
      resetFilters: () => {
        setDealerDocumentFilters({ limit: defaultLimit, offset: defaultOffset, distributorId: undefined });
      },
    },
    views: {
      [ProductEnhancementDealerDocumentsAction.DELETE]: currentView === ProductEnhancementDealerDocumentsAction.DELETE,
      [ProductEnhancementDealerDocumentsAction.ADD]: currentView === ProductEnhancementDealerDocumentsAction.ADD,
    },
  };
}

export function useProductEnhancementDealerDocumentProvider() {
  return useDataProviderPartition<ReturnType<typeof useProductEnhancementDealerDocument>>(
    'productEnhancementDealerDocumentHook'
  );
}
